import type { StrategyDefinition } from "../../../shared/types";

const NOT_PREDICTION_DISCLAIMER = "Não prevê o próximo sorteio nem altera a probabilidade de uma combinação individual.";

export const LOTOFACIL_RMS_V2: StrategyDefinition = {
  id: "lotofacil.rms_v2",
  version: "2.1.0",
  modality: "lotofacil",
  name: "RMS v2",
  shortDescription: "Carteira de 6 jogos com padrões A-B-C, exposição e sobreposição auditadas sobre os 20 concursos anteriores.",
  status: "active",
  evidence: "structural",
  ticketCount: { mode: "fixed", fixed: 6 },
  supportsBudget: false,
  supportsFixedNumbers: false,
  supportsExcludedNumbers: false,
  supportsUserSeed: false,
  supportsQualityPreset: false,
  requiresHistoricalDraws: true,
  requiresTargetContest: true,
  historyWindowSize: 20,
  optimizedMetrics: ["exposição 3-4 por dezena", "interseção 7-9 por par", "diversificação estrutural"],
  reportedMetrics: ["F11+", "F12+", "F13+", "F14+", "F15", "sobreposição", "baseline"],
  disclaimers: [
    NOT_PREDICTION_DISCLAIMER,
    "Não afirma que a RMS aumenta a chance de 15 acertos frente a outros 6 jogos distintos.",
    "O histórico define rotação/pools, não dezenas mais prováveis.",
  ],
  ux: {
    title: "Equilibrar meus 6 jogos (RMS)",
    summary: "Cria exatamente 6 jogos com distribuição equilibrada das dezenas e controle de repetição entre eles.",
    badge: "6 jogos fixos",
    helpTitle: "Como funciona Equilibrar meus 6 jogos (RMS)",
    helpBody:
      "Usa os 20 concursos anteriores para organizar os grupos de dezenas e construir uma carteira estruturalmente equilibrada. Quando duas ou mais dezenas empatam na fronteira entre grupos, o desempate usa os 50 concursos anteriores e, persistindo o empate, uma semente auditável: isso apenas torna a escolha determinística e verificável. Não prevê quais dezenas serão sorteadas e não aumenta a chance de 15 acertos em relação a outros 6 jogos distintos.",
    technicalName: "RMS v2",
  },
};

export const LOTOFACIL_MAX_DIVERSIFICATION: StrategyDefinition = {
  id: "lotofacil.max_diversification",
  version: "1.0.0",
  modality: "lotofacil",
  name: "Diversificação de carteira",
  shortDescription: "Equilibra a exposição das 25 dezenas e reduz a sobreposição entre os jogos.",
  status: "active",
  evidence: "mathematical",
  ticketCount: { mode: "range", min: 1, max: 50 },
  supportsBudget: true,
  supportsFixedNumbers: true,
  supportsExcludedNumbers: true,
  supportsUserSeed: true,
  supportsQualityPreset: false,
  requiresHistoricalDraws: false,
  requiresTargetContest: false,
  optimizedMetrics: ["equilíbrio de exposição", "sobreposição média/máxima"],
  reportedMetrics: ["F11+", "F12+", "F13+", "F14+", "F15", "baseline"],
  disclaimers: [NOT_PREDICTION_DISCLAIMER],
  ux: {
    title: "Variar mais os jogos",
    summary: "Cria jogos mais diferentes entre si, distribuindo melhor as dezenas pelo conjunto.",
    badge: "Mais diversidade",
    helpTitle: "Como funciona Variar mais os jogos",
    helpBody:
      "Busca reduzir repetições entre os jogos e equilibrar quantas vezes cada dezena aparece. Isso aumenta a diversidade do conjunto, não a probabilidade individual de uma dezena ser sorteada.",
    technicalName: "Diversificação de carteira",
  },
};

export const LOTOFACIL_MAX_COVERAGE_11: StrategyDefinition = {
  id: "lotofacil.max_coverage_11",
  version: "1.0.0",
  modality: "lotofacil",
  name: "Otimizar cobertura 11+",
  shortDescription: "Busca heurística que maximiza diretamente a cobertura de resultados com 11 acertos ou mais.",
  status: "active",
  evidence: "structural",
  ticketCount: { mode: "range", min: 1, max: 50 },
  supportsBudget: true,
  supportsFixedNumbers: true,
  supportsExcludedNumbers: true,
  supportsUserSeed: true,
  supportsQualityPreset: true,
  requiresHistoricalDraws: false,
  requiresTargetContest: false,
  optimizedMetrics: ["cobertura 11+ (amostrada durante a busca)"],
  reportedMetrics: ["F11+", "F12+", "F13+", "F14+", "F15", "baseline"],
  disclaimers: [NOT_PREDICTION_DISCLAIMER, "Melhor solução encontrada pelo algoritmo, sem prova de ótimo global."],
  ux: {
    title: "Priorizar 11 acertos ou mais",
    summary: "Organiza os jogos para cobrir mais cenários em que pelo menos um deles faria 11 ou mais acertos.",
    badge: "Busca otimizada",
    helpTitle: "Como funciona Priorizar 11 acertos ou mais",
    helpBody:
      "O algoritmo testa diferentes conjuntos de jogos e procura aquele que cobre mais resultados possíveis com 11 acertos ou mais. A busca é heurística: encontra boas soluções, mas não garante o melhor conjunto matematicamente possível.",
    technicalName: "Otimizar cobertura 11+",
  },
};

export const LOTOFACIL_MAX_COVERAGE_12: StrategyDefinition = {
  id: "lotofacil.max_coverage_12",
  version: "1.0.0",
  modality: "lotofacil",
  name: "Otimizar cobertura 12+",
  shortDescription: "Busca heurística que maximiza diretamente a cobertura de resultados com 12 acertos ou mais.",
  status: "active",
  evidence: "structural",
  ticketCount: { mode: "range", min: 1, max: 50 },
  supportsBudget: true,
  supportsFixedNumbers: true,
  supportsExcludedNumbers: true,
  supportsUserSeed: true,
  supportsQualityPreset: true,
  requiresHistoricalDraws: false,
  requiresTargetContest: false,
  optimizedMetrics: ["cobertura 12+ (amostrada durante a busca)"],
  reportedMetrics: ["F11+", "F12+", "F13+", "F14+", "F15", "baseline"],
  disclaimers: [NOT_PREDICTION_DISCLAIMER, "Melhor solução encontrada pelo algoritmo, sem prova de ótimo global."],
  ux: {
    title: "Priorizar 12 acertos ou mais",
    summary: "Organiza os jogos para cobrir mais cenários em que pelo menos um deles faria 12 ou mais acertos.",
    badge: "Busca otimizada",
    helpTitle: "Como funciona Priorizar 12 acertos ou mais",
    helpBody: "O algoritmo procura ampliar a cobertura de resultados com 12 acertos ou mais. A busca é heurística e não prevê o próximo sorteio.",
    technicalName: "Otimizar cobertura 12+",
  },
};

export const LOTOFACIL_UNIFORM_RANDOM: StrategyDefinition = {
  id: "lotofacil.uniform_random",
  version: "1.0.0",
  modality: "lotofacil",
  name: "Aleatória distinta",
  shortDescription: "Jogos gerados de forma uniforme e distinta, sem filtros estruturais ocultos.",
  status: "active",
  evidence: "baseline",
  ticketCount: { mode: "range", min: 1, max: 50 },
  supportsBudget: true,
  supportsFixedNumbers: true,
  supportsExcludedNumbers: true,
  supportsUserSeed: true,
  supportsQualityPreset: false,
  requiresHistoricalDraws: false,
  requiresTargetContest: false,
  optimizedMetrics: [],
  reportedMetrics: ["F11+", "F12+", "F13+", "F14+", "F15", "baseline"],
  disclaimers: [NOT_PREDICTION_DISCLAIMER, "Não deve ser confundida com a baseline teórica de comparação."],
  ux: {
    title: "Gerar jogos aleatórios",
    summary: "Gera jogos diferentes entre si sem aplicar filtros ou padrões.",
    badge: "Sem filtros",
    helpTitle: "Como funciona Gerar jogos aleatórios",
    helpBody:
      "Cada combinação válida é gerada de forma uniforme. Esta opção é útil como referência neutra e não deve ser confundida com a média teórica usada nas comparações.",
    technicalName: "Aleatória distinta",
  },
};

export const LOTOFACIL_STRATEGIES: StrategyDefinition[] = [
  LOTOFACIL_RMS_V2,
  LOTOFACIL_MAX_DIVERSIFICATION,
  LOTOFACIL_MAX_COVERAGE_11,
  LOTOFACIL_MAX_COVERAGE_12,
  LOTOFACIL_UNIFORM_RANDOM,
];
